import { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet } from '@/lib/api-client';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TableInfo {
  tableName: string;
  rowCount: number;
}

interface ColumnInfo {
  name: string;
  type: string;
}

interface TableData {
  columns: string[];
  columnTypes: ColumnInfo[];
  rows: Record<string, unknown>[];
  page: number;
  totalPages: number;
  totalRows: number;
}

interface CheckinSummary {
  email: string;
  display_name: string;
  total_check_ins: string;
  last_check_in: string | null;
  avg_xp: string;
  max_streak: string;
}

type ViewMode = 'tables' | 'checkins';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function DatabaseExplorer() {
  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('tables');

  // Tables list
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [tablesLoading, setTablesLoading] = useState(true);

  // Selected table data
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());

  // Check-in summaries
  const [checkins, setCheckins] = useState<CheckinSummary[]>([]);
  const [checkinsLoading, setCheckinsLoading] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---------------------------------------------------------------- */
  /*  Data fetching                                                    */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    loadTables();
  }, []);

  async function loadTables() {
    try {
      setTablesLoading(true);
      setError(null);
      const data = await apiGet<TableInfo[]>('/api/admin/database/tables');
      setTables(data);
    } catch (err) {
      console.error('Error loading tables:', err);
      setError('Failed to load database tables');
    } finally {
      setTablesLoading(false);
    }
  }

  const loadTableData = useCallback(
    async (tableName: string, pageNum: number, search: string) => {
      try {
        setTableLoading(true);
        setError(null);
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        const data = await apiGet<TableData>(
          `/api/admin/database/tables/${tableName}?page=${pageNum}&limit=20${searchParam}`
        );
        setTableData(data);
      } catch (err) {
        console.error('Error loading table data:', err);
        setError(`Failed to load data for table "${tableName}"`);
      } finally {
        setTableLoading(false);
      }
    },
    []
  );

  async function loadCheckins() {
    try {
      setCheckinsLoading(true);
      setError(null);
      const data = await apiGet<CheckinSummary[]>('/api/admin/database/check-ins');
      setCheckins(data);
    } catch (err) {
      console.error('Error loading check-ins:', err);
      setError('Failed to load check-in summaries');
    } finally {
      setCheckinsLoading(false);
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Handlers                                                         */
  /* ---------------------------------------------------------------- */

  function handleSelectTable(tableName: string) {
    setSelectedTable(tableName);
    setPage(1);
    setSearchQuery('');
    setExpandedCells(new Set());
    loadTableData(tableName, 1, '');
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (selectedTable) {
        setPage(1);
        loadTableData(selectedTable, 1, value);
      }
    }, 400);
  }

  function handlePageChange(newPage: number) {
    if (!selectedTable || !tableData) return;
    if (newPage < 1 || newPage > tableData.totalPages) return;
    setPage(newPage);
    loadTableData(selectedTable, newPage, searchQuery);
  }

  function handleToggleView(mode: ViewMode) {
    setViewMode(mode);
    setError(null);
    if (mode === 'checkins') {
      loadCheckins();
    }
  }

  function toggleCellExpansion(key: string) {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  /* ---------------------------------------------------------------- */
  /*  Helpers                                                          */
  /* ---------------------------------------------------------------- */

  function formatCellValue(value: unknown): string {
    if (value === null || value === undefined) return 'NULL';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  function truncateValue(value: string, cellKey: string): React.ReactNode {
    if (value.length <= 100 || expandedCells.has(cellKey)) {
      return (
        <span>
          {value}
          {value.length > 100 && (
            <button
              onClick={() => toggleCellExpansion(cellKey)}
              className="ml-2 text-xs text-amber-400 hover:text-amber-300 underline"
            >
              Show less
            </button>
          )}
        </span>
      );
    }

    return (
      <span>
        {value.slice(0, 100)}
        <button
          onClick={() => toggleCellExpansion(cellKey)}
          className="ml-1 text-xs text-amber-400 hover:text-amber-300 underline"
        >
          ...show more
        </button>
      </span>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render: Loading spinner                                          */
  /* ---------------------------------------------------------------- */

  function renderSpinner() {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render: Tables view                                              */
  /* ---------------------------------------------------------------- */

  function renderTablesView() {
    return (
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar: table list */}
        <div className="lg:w-72 flex-shrink-0">
          <div className="glass-strong rounded-xl overflow-hidden">
            <div className="p-4 bg-charcoal-800/50 border-b border-charcoal-700">
              <h3 className="font-semibold text-white">
                Tables ({tables.length})
              </h3>
            </div>

            {tablesLoading ? (
              renderSpinner()
            ) : tables.length === 0 ? (
              <div className="p-6 text-center text-slate-400">
                No tables found
              </div>
            ) : (
              <div className="divide-y divide-charcoal-700/50 max-h-[600px] overflow-y-auto">
                {tables.map((table) => (
                  <button
                    key={table.tableName}
                    onClick={() => handleSelectTable(table.tableName)}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      selectedTable === table.tableName
                        ? 'bg-amber-500/20 border-l-2 border-amber-500'
                        : 'hover:bg-charcoal-800/30 border-l-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-sm font-medium ${
                          selectedTable === table.tableName
                            ? 'text-amber-300'
                            : 'text-white'
                        }`}
                      >
                        {table.tableName}
                      </span>
                      <span className="text-xs text-slate-500 tabular-nums">
                        {table.rowCount.toLocaleString()} rows
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main: table data viewer */}
        <div className="flex-1 min-w-0">
          {!selectedTable ? (
            <div className="glass-strong rounded-xl p-12 text-center">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-slate-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 0v1.5c0 .621-.504 1.125-1.125 1.125"
                />
              </svg>
              <p className="text-lg font-medium text-slate-300 mb-1">
                Select a table
              </p>
              <p className="text-sm text-slate-500">
                Choose a table from the sidebar to browse its data
              </p>
            </div>
          ) : (
            renderTableViewer()
          )}
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render: Table viewer (data grid)                                 */
  /* ---------------------------------------------------------------- */

  function renderTableViewer() {
    return (
      <div className="glass-strong rounded-xl overflow-hidden">
        {/* Header with search */}
        <div className="p-4 bg-charcoal-800/50 border-b border-charcoal-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-white">{selectedTable}</h3>
            {tableData && (
              <p className="text-xs text-slate-500 mt-0.5">
                {tableData.totalRows.toLocaleString()} total rows
              </p>
            )}
          </div>

          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search rows..."
              className="w-full sm:w-64 pl-9 pr-4 py-2 bg-charcoal-900 border border-charcoal-700/50 rounded-lg
                         text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500
                         transition-colors"
            />
          </div>
        </div>

        {/* Table content */}
        {tableLoading ? (
          renderSpinner()
        ) : !tableData || tableData.rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            {searchQuery ? 'No rows match your search' : 'This table is empty'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-charcoal-700">
                    {tableData.columns.map((col) => (
                      <th
                        key={col}
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider
                                   bg-charcoal-900/50 whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-charcoal-700/50">
                  {tableData.rows.map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className="hover:bg-charcoal-800/30 transition-colors"
                    >
                      {tableData.columns.map((col) => {
                        const cellKey = `${rowIdx}-${col}`;
                        const raw = formatCellValue(row[col]);
                        return (
                          <td
                            key={col}
                            className="px-4 py-3 text-slate-300 whitespace-nowrap max-w-xs"
                          >
                            {truncateValue(raw, cellKey)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {tableData.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-charcoal-700 bg-charcoal-900/30">
                <p className="text-xs text-slate-500">
                  Page {tableData.page} of {tableData.totalPages}
                </p>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                    className="px-3 py-1.5 text-sm rounded-lg transition-colors
                               bg-charcoal-800 border border-charcoal-700/50 text-slate-300
                               hover:bg-charcoal-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= tableData.totalPages}
                    className="px-3 py-1.5 text-sm rounded-lg transition-colors
                               bg-charcoal-800 border border-charcoal-700/50 text-slate-300
                               hover:bg-charcoal-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render: Check-ins view                                           */
  /* ---------------------------------------------------------------- */

  function renderCheckinsView() {
    return (
      <div className="glass-strong rounded-xl overflow-hidden">
        <div className="p-4 bg-charcoal-800/50 border-b border-charcoal-700">
          <h3 className="font-semibold text-white">
            User Check-in Summary
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Aggregated check-in statistics per user
          </p>
        </div>

        {checkinsLoading ? (
          renderSpinner()
        ) : checkins.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            No check-in data available
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-charcoal-700">
                  {['User', 'Email', 'Total Check-ins', 'Last Check-in', 'Avg XP', 'Max Streak'].map(
                    (header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider
                                   bg-charcoal-900/50 whitespace-nowrap"
                      >
                        {header}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal-700/50">
                {checkins.map((row, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-charcoal-800/30 transition-colors"
                  >
                    <td className="px-4 py-3 text-white font-medium whitespace-nowrap">
                      {row.display_name || '--'}
                    </td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                      {row.email}
                    </td>
                    <td className="px-4 py-3 text-slate-300 tabular-nums">
                      {Number(row.total_check_ins).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                      {row.last_check_in
                        ? new Date(row.last_check_in).toLocaleDateString()
                        : '--'}
                    </td>
                    <td className="px-4 py-3 text-slate-300 tabular-nums">
                      {Number(row.avg_xp).toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-slate-300 tabular-nums">
                      {row.max_streak}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Main render                                                      */
  /* ---------------------------------------------------------------- */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white mb-2">
            Database Explorer
          </h2>
          <p className="text-slate-400 text-sm">
            Browse database tables and view user check-in analytics
          </p>
        </div>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-1 bg-charcoal-900 rounded-lg p-1 w-fit">
        <button
          onClick={() => handleToggleView('tables')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            viewMode === 'tables'
              ? 'bg-amber-500 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Tables
        </button>
        <button
          onClick={() => handleToggleView('checkins')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            viewMode === 'checkins'
              ? 'bg-amber-500 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Check-ins
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
          {error}
        </div>
      )}

      {/* Content */}
      {viewMode === 'tables' ? renderTablesView() : renderCheckinsView()}
    </div>
  );
}
